import * as React from 'react';
import { useEffect } from 'react';
import useCollapse from 'react-collapsed';
import { UseCollapseOutput } from 'react-collapsed/dist/types';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { Breakpoint, IconName, Language, ValidationBarTheme } from '../../enums';
import { useFormatDate, useParsers } from '../../hooks';
import { 
  PurchasedArtworkSummary, 
  Status, 
  UseArtworkBoxSoldSummary, 
  UseFormatDate, 
  ValidationBarSettings, 
  DeliveryDetailsSettings, 
  UseParsers
} from '../../interfaces';
import { Badge } from '../badge/badge.component';

import { useArtworkBoxSoldSummaryHook } from './artworkBoxSoldSummary.hook';
import { 
  ArtworkContainerPrice,
  ArtworkContainerWrapper,
  ArtworksContainer, 
  BadgesContainer, 
  Container, 
  DateContainer, 
  DetailsBox, 
  DetailsButton, 
  DetailsContainer, 
  DetailsContent, 
  DetailsTitle, 
  DetailsWrapper, 
  FinalPrice, 
  FinalPriceContainer, 
  Header, 
  ItemDetailsContainer, 
  ItemSummary, 
  ItemSummaryDetails, 
  MobileItemSummary, 
  MobileItemSummaryContainer, 
  PayButton, 
  StyledArtworkContainer, 
  StyledIcon, 
  StyledValidationBar
} from './artworkBoxSoldSummary.styled';
import { ArtworkBoxSoldSummaryProps } from './artworkBoxSoldSummary.types';

export const ArtworkBoxSoldSummary: React.FC<ArtworkBoxSoldSummaryProps> = (props: ArtworkBoxSoldSummaryProps): JSX.Element => {
  const {
    createdDate,
    badges,
    items,
    ordersDetails,
    payAction,
    finalPrice,
    isExpandedOnInit = false,
    className
  }: ArtworkBoxSoldSummaryProps = props;
  const { formatDate }: UseFormatDate = useFormatDate(Language.Pl);
  const { getCollapseProps, setExpanded, isExpanded }: UseCollapseOutput = useCollapse({ defaultExpanded: isExpandedOnInit });
  const { deliveryDetailsConfig }: UseArtworkBoxSoldSummary = useArtworkBoxSoldSummaryHook(ordersDetails);
  const { parsePrice }: UseParsers = useParsers();
  const { t }: TransProps<never> = useTranslation();
  const isSmallMobile: boolean = useMediaQuery({ query: Breakpoint.SmallMobile });
  const currency: string = 'zł';

  const onToggleAction: () => void = (): void => {
    setExpanded(!isExpanded);
  };

  useEffect((): void => setExpanded(isExpandedOnInit), [isExpandedOnInit]);

  const validationTheme: (
    item: PurchasedArtworkSummary
  ) => ValidationBarSettings | undefined = (item: PurchasedArtworkSummary): ValidationBarSettings | undefined => {
    if (payAction) {
      return;
    }

    if (item.actions?.deliveryConfirmAction) {
      return {
        message: t('onarte.ui.artworkBoxSoldSummary.validationTheme.deliveryConfirmAction.message'),
        barTheme: ValidationBarTheme.Information,
        buttonData: {
          action: item.actions.deliveryConfirmAction
        },
        buttonLabel: t('onarte.ui.artworkBoxSoldSummary.validationTheme.deliveryConfirmAction.buttonLabel'),
      }; 
    } else if (item.actions?.orderDeliveryAction) {
      return {
        message: t('onarte.ui.artworkBoxSoldSummary.validationTheme.orderDeliveryAction.message'),
        barTheme: ValidationBarTheme.Information,
        buttonData: {
          action: item.actions.orderDeliveryAction
        },
        buttonLabel: t('onarte.ui.artworkBoxSoldSummary.validationTheme.orderDeliveryAction.buttonLabel'),
      };
    } else if (item.trackingUrl && item.trackingId) {
      return {
        message: t('onarte.ui.artworkBoxSoldSummary.validationTheme.trackingUrl.message', { value: item.trackingId}),
        barTheme: ValidationBarTheme.Information,
        buttonData: {
          externalUrl: item.trackingUrl
        },
        buttonLabel: t('onarte.ui.artworkBoxSoldSummary.validationTheme.trackingUrl.buttonLabel'),
      };
    } else if (item.isDeliveryCompleted) {
      return {
        message: t('onarte.ui.artworkBoxSoldSummary.validationTheme.deliveryCompleted.message'),
        barTheme: ValidationBarTheme.Success,
      };
    } else {
      return {
        message: t('onarte.ui.artworkBoxSoldSummary.validationTheme.deliveryInprogress.message'),
        barTheme: ValidationBarTheme.Information,
      };
    }
  };

  return (
    <Container className={className}>
      <Header>
        <BadgesContainer>
          {badges.map((badge: Status): JSX.Element => <Badge {...badge} key={badge.label} />)}
          {!!payAction && (
            <PayButton onClick={payAction}>
              <StyledIcon name={IconName.Money} size={24} />
              {t('onarte.ui.artworkBoxSoldSummary.payButton.label')}
            </PayButton>
          )}
        </BadgesContainer>
        <DateContainer>
          {formatDate(createdDate, 'DD MMM YYYY, HH:mm:ss')}
        </DateContainer>
      </Header>
      <ArtworksContainer>
        {items.map((item: PurchasedArtworkSummary): JSX.Element => (
          <ItemDetailsContainer key={item.artworkDetails.itemName}>
            <ArtworkContainerWrapper>
              <StyledArtworkContainer 
                artworkDetails={{
                  boxTitleDetails: item.artworkDetails,
                  image: item.image
                }} 
              />
              {!isSmallMobile && <ArtworkContainerPrice>{parsePrice(item.costsDetails.price)} {currency}</ArtworkContainerPrice>}
            </ArtworkContainerWrapper>
            {isSmallMobile && (
              <ItemSummary>
                {t('onarte.ui.artworkBoxSoldSummary.itemSummary.price')}
                <ItemSummaryDetails>{parsePrice(item.costsDetails.price)} {currency}</ItemSummaryDetails>
              </ItemSummary>
            )}
            <ItemSummary>
              {t('onarte.ui.artworkBoxSoldSummary.itemSummary.commision')}
              <ItemSummaryDetails>
                {item.costsDetails.commission ? `${parsePrice(item.costsDetails.commission)} ${currency}` : '-'}
              </ItemSummaryDetails>
            </ItemSummary>
            <ItemSummary>
              {t('onarte.ui.artworkBoxSoldSummary.itemSummary.deliveryType', { value: item.costsDetails.deliveryTypeLabel })}
              <ItemSummaryDetails>
                {item.costsDetails.deliveryCost ? `${parsePrice(item.costsDetails.deliveryCost)} ${currency}` :  '-'}
              </ItemSummaryDetails>
            </ItemSummary>
            {(!isSmallMobile && !!item.sellerEmail) && (
              <ItemSummary>
                {t('onarte.ui.artworkBoxSoldSummary.itemSummary.sellerEmail')}
                <ItemSummaryDetails>{item.sellerEmail}</ItemSummaryDetails>
              </ItemSummary>
            )}
            {!isSmallMobile && (
              <ItemSummary>
                {t('onarte.ui.artworkBoxSoldSummary.itemSummary.commentToSeller')}
                <ItemSummaryDetails>{item.commentToSeller || '-'}</ItemSummaryDetails>
              </ItemSummary>
            )}
            {isSmallMobile && (
              <MobileItemSummaryContainer>
                {!!item.sellerEmail && (
                  <MobileItemSummary 
                    text={t('onarte.ui.artworkBoxSoldSummary.itemSummary.mobileSellerEmail', { value: item.sellerEmail })} 
                  />
                )}
                <MobileItemSummary
                  text={t('onarte.ui.artworkBoxSoldSummary.itemSummary.mobileCommentToSeller', { value: item.commentToSeller })}
                />
              </MobileItemSummaryContainer>
            )}
            {!!validationTheme && <StyledValidationBar {...validationTheme(item)} />}
          </ItemDetailsContainer>
        ))}
        {!!finalPrice && (
          <FinalPriceContainer>
            {t('onarte.ui.artworkBoxSoldSummary.finalPriceContainer')}
            <FinalPrice data-testid='artwork-box-sold-summary-final-price'>{parsePrice(finalPrice)} {currency}</FinalPrice>
          </FinalPriceContainer>
        )}
        <DetailsContainer {...getCollapseProps()}>
          <DetailsWrapper>
            {deliveryDetailsConfig.map((config: DeliveryDetailsSettings): JSX.Element => (
              <DetailsBox key={config.key}>
                <DetailsTitle>{config.key}</DetailsTitle>
                <DetailsContent text={config.value} />
              </DetailsBox>
            ))}
          </DetailsWrapper>
        </DetailsContainer>
      </ArtworksContainer>
      <DetailsButton onClick={onToggleAction}>
        {isExpanded 
          ? t('onarte.ui.artworkBoxSoldSummary.detailsButton.collapse')
          : t('onarte.ui.artworkBoxSoldSummary.detailsButton.expand')
        }
      </DetailsButton>
    </Container>
  );
};
